import {useCallback, useEffect, useState} from 'react';
import {Box, IconButton, Typography} from '@mui/material';
import {t} from 'i18next';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import {getEcaSummary, getTagData} from '../../../api/EcaManagementService';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {ReactComponent as BackgroundBlur} from '../../../assests/svg/backgroundBlur.svg';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {Link, useNavigate} from 'react-router-dom';
import {
  formatNumberWithComma,
  getUTCDateTimeForDisplay,
  isEmpty,
} from '../../../utils/utils';
import EcaBottomSection from './EcaBottomSection';
import {ReactComponent as IndexBannerInfoIcon} from '../../../../src/assests/svg/index_info.svg';
import {
  LoadFileData,
  TagData,
} from '../../client-details/project-data-table/types';
import {
  setIsSearchFromECAHome,
  setRelationalGroupsList,
} from '../../../redux/slices/ecaAdhocSearchSlice';
import {ONLY_DATE_SHOW_FORMAT, SOURCE_TYPE} from '../../../constants/constants';
import {getRelationalGroups} from '../../../api/EcaServices';
import {RelationalGroup} from '../../pages/index-search/RelationshipGroupPopup';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {AxiosError} from 'axios';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
} from '../../../constants/analyticsConstants';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../hooks/permissions/usePermissionsByPage';

const EcaOverview = () => {
  const navigate = useNavigate();
  const [ecaSummaryData, setEcaSummaryData] = useState<LoadFileData | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [, setIsFailedToLoadContent] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [relationalGroupArray, setRelationalGroupArray] = useState<
    RelationalGroup[]
  >([]);

  const [resetTagFlag, setResetTagFlag] = useState(false);

  const {showToast} = useToast();
  const {trackGAEvent} = useAnalytics();

  const {hasSearchesPerm, hasExportPerm, hasTaggingDocumentPerm} =
    usePermissionsByPage(PAGE_NAME.ECA_OVERVIEW);

  const ClientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const setLoadFiles: any = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );
  const {selectedLoadFile} = useSelector(
    (state: RootState) => state.ecaAdhocSearch
  );

  const data: any[] = (setLoadFiles && setLoadFiles.loadFiles) || [];

  const latestItem = data.length
    ? data.reduce((latest: any, current: any) => {
        const latestDate = new Date(latest.lastLoadDateTime);
        const currentDate = new Date(current.lastLoadDateTime);
        return currentDate > latestDate ? current : latest;
      })
    : undefined;

  const checkDate: string | undefined =
    selectedLoadFile?.lastLoadDateTime ||
    (latestItem ? latestItem.lastLoadDateTime : undefined);

  const formattedDate = checkDate
    ? getUTCDateTimeForDisplay(checkDate, ONLY_DATE_SHOW_FORMAT)
    : '';
  const dispatch = useDispatch();

  const {projectId, selectedProjectName: projectName} = useSelector(
    (state: RootState) => state.reports
  );

  useEffect(() => {
    fetchEcaSummary();
    if (ClientId && projectId) {
      fetchRelationGroups();
    }
  }, [ClientId, projectId]);

  const isTagListEmpty = useCallback(
    (tagData: TagData[]) =>
      isEmpty(tagData) ||
      !(tagData ?? []).some((field) => !isEmpty(field?.values ?? [])),
    []
  );

  const fetchTagData = useCallback(
    async (activeLoadFile?: LoadFileData) => {
      if (ClientId && projectId) {
        setLoading(true);
        try {
          const paramString = `clientId=${ClientId}&projectId=${projectId}`;
          const response = await getTagData(paramString);
          const res = response.data;
          const tagData = res.data;
          if (!isTagListEmpty(tagData)) {
            navigate('/index-search', {
              state: {
                source: SOURCE_TYPE.notTagged,
                tagData,
                ...(activeLoadFile && {selected: activeLoadFile}),
              },
            });
          } else {
            showToast(
              t('eca.overview.noTagsFound'),
              'success',
              undefined,
              'center'
            );
          }
        } catch (error) {
          const err = error as AxiosError;
          showToast(err?.message, 'error', undefined, 'center', undefined, {
            code: err?.code,
          });
        } finally {
          setLoading(false);
        }
      }
    },
    [ClientId, isTagListEmpty, navigate, projectId, showToast]
  );

  const fetchEcaSummary = async () => {
    setLoading(true);
    setIsFailedToLoadContent(false);
    try {
      const paramString = `clientId=${ClientId}&projectid=${projectId}`;
      const response = await getEcaSummary(paramString);
      const res = await response.data;
      const data = res?.data;
      setEcaSummaryData(data);
      setResetTagFlag(true);
    } catch (error) {
      setIsFailedToLoadContent(true);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelationGroups = async () => {
    try {
      const params = `?clientId=${ClientId}&projectId=${projectId}`;
      let relationalGroups: RelationalGroup[] = [];
      const result = await getRelationalGroups(params);
      relationalGroups = (result?.data?.data ?? []).map(
        (group: RelationalGroup, index: number) => {
          return {
            ...group,
            index: index + 1,
          };
        }
      );
      setRelationalGroupArray([...relationalGroups]);
      dispatch(setRelationalGroupsList([...relationalGroups]));
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <>
      <div className="eca-homepage">
        <BackgroundBlur aria-hidden="true" />
        <div className="eca-project">
          <Box display={'flex'} justifyContent={'space-between'}>
            <Typography
              className="typo-text subhead2_size first-text welcome-title-color"
              data-testid="welcome-text"
              component="h1"
            >
              {projectName}
            </Typography>
          </Box>
        </div>
        <Box
          className="header-items"
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <div>
            <Typography
              className="typo-text subhead4_size first-text"
              data-testid="welcome-text"
            >
              {t('eca.overview.total')}
            </Typography>

            <div className="total-head">
              <Typography
                className="typo-text display1_size second-text"
                data-testid="total-count"
              >
                {formatNumberWithComma(
                  ecaSummaryData?.totalDocumentsCount ?? 0
                )}
              </Typography>
              {hasSearchesPerm && (
                <Link
                  to="/index-search"
                  aria-label={t('eca.overview.coreEciIndexSearch')}
                  data-testid="link-index-search"
                  onClick={() => {
                    dispatch(setIsSearchFromECAHome(true));
                  }}
                >
                  <div className="details">
                    <AurIcon icon={IconNames.Search} />
                  </div>
                </Link>
              )}
            </div>
          </div>

          {hasSearchesPerm && (
            <Box className="eca-tiles">
              {hasTaggingDocumentPerm && (
                <>
                  <div className="tile">
                    <IconButton
                      className="tile-item"
                      role="button"
                      data-testid="btn-not-tagged"
                      onClick={() => {
                        fetchTagData();
                        trackGAEvent({
                          eventName: GA_EVENT_NAMES.SEARCH,
                          click: {
                            name: t('gaTracking.ecaHomePage.docNotTagged'),
                            region: GA_REGIONS.TOP_NAV,
                            type: GA_CLICK_TYPES.TILE,
                            action: t('gaTracking.ecaHomePage.searchDocuments'),
                          },
                        });
                      }}
                    >
                      <div className="count-head">
                        <Typography className="count typo-text headline2_size">
                          {formatNumberWithComma(
                            ecaSummaryData?.unTaggedDocumentCount ?? 0
                          )}
                        </Typography>
                        <AurIcon icon={IconNames.ChevronRight} />
                      </div>
                      <Typography className="description typo-text subhead4_size">
                        {t('eca.overview.notTagged')}
                      </Typography>
                    </IconButton>
                    <div className="tile-item"></div>
                  </div>
                  <div className="tile-divider"></div>
                </>
              )}
              {hasExportPerm && (
                <div className="tile">
                  <IconButton
                    className="tile-item"
                    role="button"
                    data-testid="btn-not-exported"
                    onClick={() => {
                      navigate('/index-search', {
                        state: {
                          source: SOURCE_TYPE.notExported,
                        },
                      });
                      trackGAEvent({
                        eventName: GA_EVENT_NAMES.SEARCH,
                        click: {
                          name: t('gaTracking.ecaHomePage.notExported'),
                          region: GA_REGIONS.TOP_NAV,
                          type: GA_CLICK_TYPES.TILE,
                          action: t('gaTracking.ecaHomePage.searchDocuments'),
                        },
                      });
                    }}
                  >
                    <div className="count-head">
                      <Typography className="count typo-text headline2_size">
                        {formatNumberWithComma(
                          ecaSummaryData?.unExportedDocumentCount ?? 0
                        )}
                      </Typography>
                      <AurIcon icon={IconNames.ChevronRight} />
                    </div>
                    <Typography className="description typo-text subhead4_size">
                      {t('eca.overview.notExported')}
                    </Typography>
                  </IconButton>
                  <div className="tile-item"></div>
                </div>
              )}
              {hasExportPerm && (
                <>
                  <div className="tile-divider"></div>
                  <div className="tile">
                    <IconButton
                      className="tile-item"
                      data-testid="btn-exported"
                      role="button"
                      onClick={() => {
                        navigate('/index-search', {
                          state: {
                            source: SOURCE_TYPE.exported,
                          },
                        });
                        trackGAEvent({
                          eventName: GA_EVENT_NAMES.SEARCH,
                          click: {
                            name: t('gaTracking.ecaHomePage.exported'),
                            region: GA_REGIONS.TOP_NAV,
                            type: GA_CLICK_TYPES.TILE,
                            action: t('gaTracking.ecaHomePage.searchDocuments'),
                          },
                        });
                      }}
                    >
                      <div className="count-head">
                        <Typography className="count typo-text headline2_size">
                          {formatNumberWithComma(
                            ecaSummaryData?.exportedDocumentCount ?? 0
                          )}
                        </Typography>
                        <AurIcon icon={IconNames.ChevronRight} />
                      </div>
                      <Typography className="description typo-text subhead4_size">
                        {t('eca.overview.exported')}
                      </Typography>
                    </IconButton>
                  </div>
                </>
              )}
            </Box>
          )}
        </Box>
        <Box className="indication margin-bottom">
          <Typography className="typo-text body3_size declaimerSpace mist-1400">
            {t('welcomeScreenSingleClient.lastLoad')}
            <AurLightTooltip title={t('common.yearMonthDate')}>
              <span className="weight-700">
                {` ${formattedDate || ' -- '} `} &nbsp;&nbsp;&nbsp;
              </span>
            </AurLightTooltip>
            <span className="mist-1200">{t('common.dataCurrentAsOf')}</span>
            <AurLightTooltip title={t('common.yearMonthDate')}>
              <span className="color-mist-1300">
                {` ${getUTCDateTimeForDisplay(new Date())} `}
              </span>
            </AurLightTooltip>
          </Typography>

          <Typography className="typo-text body3_size mist-1200">
            <IndexBannerInfoIcon
              style={{
                width: '13.33px',
                height: '13.33px',
                fill: '#8C9EBA',
                stroke: '#8C9EBA',
                marginRight: '4px',
              }}
              aria-label={t('welcomeScreenSingleClient.ecaOverviewDisclaimer')}
            />
            {t('welcomeScreenSingleClient.disclaimerText')}
          </Typography>
        </Box>
        <AurLoadingIndicator overlay={true} isOpen={loading} />
      </div>
      <EcaBottomSection
        ecaSummaryData={ecaSummaryData}
        onClickDocsNotTaggedSection={fetchTagData}
        resetTagFlag={resetTagFlag}
        onResetTagHandled={() => setResetTagFlag(false)}
      />
    </>
  );
};

export default EcaOverview;
